/**
 * Help Center Content
 * Location: frontend/src/data/helpContent.js
 *
 * Contains all help articles, organized by category, for the RLC Spark Help Center.
 * Articles are written in Markdown format and include tags for search.
 */

// ============================================
// HELP CONTENT STRUCTURE
// ============================================

export const HELP_CONTENT = {
  'getting-started': {
    title: 'Getting Started',
    description: 'Learn the basics of RLC Spark',
    articles: [
      {
        id: 'what-is-rlc-spark',
        title: 'What is RLC Spark?',
        description: 'Introduction to the RLC Spark platform and its features',
        tags: ['basics', 'introduction', 'overview'],
        estimatedReadTime: '3 min',
        content: `
# What is RLC Spark?

RLC Spark is an AI-powered coaching platform designed to help you make better decisions through **Reflective Learning Conversations** (RLC).

## Core Features

### 1. AI Coach
Your personal AI coach that uses the RLC methodology to guide you through key decisions and learning moments.

![AI Coach Interface](/help-images/06-ai-coach-interface.png)

### 2. Report Writer
Generate comprehensive reports documenting your key decisions, insights, and action plans.

### 3. Archive
Store and search your documents, conversations, and reports using powerful semantic search.

## How It Works

1. **Ask Questions** - Engage with your AI Coach about challenges, decisions, or learning opportunities
2. **Reflect Deeply** - The coach guides you through structured reflection using the RLC methodology
3. **Document Insights** - Capture your learnings in reports
4. **Build Knowledge** - Your archive grows into a personalized knowledge base

## Getting Help

- Press **Cmd+Shift+H** (Mac) or **Ctrl+Shift+H** (Windows) to open this help center anytime
- Use the search box to find specific topics
- Browse categories in the sidebar

---

**Next:** Learn how to [log in to your account](#quick-start-login)
        `
      },
      {
        id: 'quick-start-login',
        title: 'Logging In',
        description: 'How to access your RLC Spark account',
        tags: ['login', 'authentication', 'getting started'],
        estimatedReadTime: '2 min',
        content: `
# Logging In to RLC Spark

## Step 1: Navigate to Login

Go to your RLC Spark URL and you'll see the login screen:

![Login Screen](/help-images/01-login-screen.png)

## Step 2: Enter Your Credentials

- **Email:** The email address provided by your administrator
- **Password:** Your secure password

> 💡 **Tip:** Your password must be at least 8 characters and include uppercase, lowercase, and numbers.

## Step 3: Access Your Dashboard

After successful login, you'll be directed to your main dashboard where you can access all features.

![Dashboard](/help-images/04-dashboard-main.png)

## Troubleshooting Login Issues

### "Invalid Credentials" Error

- Double-check your email and password
- Ensure Caps Lock is off
- Try resetting your password using the "Forgot Password?" link

### Account Locked

After 5 failed login attempts, your account will be locked for security. Wait 15 minutes or contact your administrator.

### Can't Remember Password

Click "Forgot Password?" on the login screen and follow the email instructions to reset it.

---

**Next:** Learn about [navigating the interface](#navigating-interface)
        `
      },
      {
        id: 'navigating-interface',
        title: 'Navigating the Interface',
        description: 'Understanding the RLC Spark interface and navigation',
        tags: ['navigation', 'interface', 'basics'],
        estimatedReadTime: '3 min',
        content: `
# Navigating the Interface

## Main Navigation Bar

![Navigation Bar](/help-images/05-navigation-bar.png)

The navigation bar at the top provides quick access to all main features:

1. **AI Coach** - Start or continue conversations with your AI coach
2. **Report Writer** - Create and manage reports
3. **Archive** - Access your document library
4. **User Menu** - Account settings and logout

## Dashboard Overview

![Dashboard](/help-images/04-dashboard-main.png)

Your dashboard shows:
- Quick access to recent conversations
- Recent reports
- Suggested next actions
- System notifications

## Mobile Interface

RLC Spark is fully responsive and works on all devices:

**Tablet View:**
![Tablet View](/help-images/11-mobile-tablet.png)

**Mobile Phone:**
![Mobile View](/help-images/12-mobile-phone.png)

On mobile devices:
- Navigation collapses into a menu icon
- Content adapts to smaller screens
- All features remain accessible

## Keyboard Shortcuts

Press \`?\` or \`Cmd+Shift+H\` anytime to see all keyboard shortcuts.

---

**Next:** Learn how to [use the AI Coach](#ai-coach-basics)
        `
      },
      {
        id: 'first-conversation',
        title: 'Your First Conversation',
        description: 'How to start and manage conversations with your AI Coach',
        tags: ['ai coach', 'conversation', 'tutorial'],
        estimatedReadTime: '4 min',
        content: `
# Your First Conversation with AI Coach

## Starting a Conversation

1. Click **AI Coach** in the navigation
2. You'll see the conversation interface with a message input at the bottom

![AI Coach Interface](/help-images/06-ai-coach-interface.png)

## Asking Effective Questions

The AI Coach works best when you provide context. Instead of:
- ❌ "Help me with my project"

Try:
- ✅ "I'm starting a new software project and need to decide between React and Vue. Can you help me think through this decision?"

## What Makes a Good Conversation

**Be Specific:**
- Share relevant context
- Explain your constraints
- Mention what you've already tried

**Be Open:**
- The coach will ask clarifying questions
- Reflect honestly on your responses
- Be willing to explore different perspectives

## Managing Conversations

- **Save Conversations:** All conversations are automatically saved
- **Search Past Conversations:** Use the Archive to find previous discussions
- **Export Conversations:** Download as PDF or Markdown

## Voice Input (Optional)

If available, click the microphone icon to use voice input for hands-free conversation.

---

**Next:** Explore [AI Coach advanced features](#ai-coach-advanced)
        `
      },
    ]
  },

  'ai-coach': {
    title: 'AI Coach',
    description: 'Master the AI Coach feature',
    articles: [
      {
        id: 'ai-coach-basics',
        title: 'AI Coach Basics',
        description: 'Understanding how to use the AI Coach effectively',
        tags: ['ai coach', 'basics', 'conversation'],
        estimatedReadTime: '5 min',
        content: `
# AI Coach Basics

## What is the AI Coach?

The AI Coach is your intelligent companion for reflective learning conversations. It uses the **RLC (Reflective Learning Conversations)** methodology to help you:

- Make better decisions
- Learn from experiences
- Identify knowledge gaps
- Develop action plans

![AI Coach Interface](/help-images/06-ai-coach-interface.png)

## Starting a Conversation

1. Click **AI Coach** in the main navigation
2. Type your question or topic in the message input
3. Press Enter or click Send

## The RLC Methodology

The coach follows a structured approach:

### 1. Understand the Situation
The coach helps you articulate what's happening and why it matters.

### 2. Explore Perspectives
You'll examine the situation from multiple angles and identify assumptions.

### 3. Identify Learning
What new insights have emerged? What patterns do you notice?

### 4. Plan Actions
What will you do differently? What specific steps will you take?

## Best Practices

**Do:**
- ✅ Provide context and background
- ✅ Be specific about your challenges
- ✅ Engage honestly with reflection questions
- ✅ Take time to think through responses

**Don't:**
- ❌ Expect instant answers without reflection
- ❌ Skip the coach's clarifying questions
- ❌ Rush through the conversation
- ❌ Treat it like a simple Q&A

## Conversation Features

- **Auto-save:** Every conversation is saved automatically
- **History:** Access past conversations anytime
- **Export:** Download conversations for your records
- **Voice Input:** Use voice-to-text for hands-free interaction

---

**Next:** Learn about [conversation modes](#ai-coach-modes)
        `
      },
      {
        id: 'ai-coach-advanced',
        title: 'Advanced AI Coach Features',
        description: 'Power user tips and advanced conversation techniques',
        tags: ['ai coach', 'advanced', 'tips'],
        estimatedReadTime: '4 min',
        content: `
# Advanced AI Coach Features

## Voice Input

Use your voice to interact with the coach:

1. Click the microphone icon in the message input
2. Speak your message
3. The text will appear automatically
4. Click the arrow to send or the X to cancel

## Message Actions

Each AI response has action buttons:

- **Copy** - Copy the message to clipboard
- **Try Again** - Regenerate the response
- **👍/👎** - Provide feedback on response quality

## Text Size Control

Adjust the conversation text size for better readability:
- **Small** - Compact view (13px)
- **Medium** - Default view (15px)
- **Large** - Comfortable reading (17px)

## Conversation History

Access your previous conversations:
1. Go to Archive
2. Filter by "Conversations"
3. Search by topic or date
4. Click to reopen any conversation

## Exporting Conversations

Download your conversations for offline access:

1. Open a conversation
2. Click the menu icon (⋯)
3. Select **Export**
4. Choose format: PDF or Markdown

## Pro Tips

**For Complex Decisions:**
- Break into multiple focused conversations
- Revisit and refine over time
- Link related conversations in your notes

**For Learning:**
- Use the coach immediately after experiences
- Capture insights while they're fresh
- Review past conversations monthly

**For Teams:**
- Share conversation exports
- Use insights in team discussions
- Build shared understanding

---

**Next:** Learn about the [Report Writer](#report-writer-basics)
        `
      },
    ]
  },

  'report-writer': {
    title: 'Report Writer',
    description: 'Create and manage reports',
    articles: [
      {
        id: 'report-writer-basics',
        title: 'Creating Your First Report',
        description: 'Step-by-step guide to creating reports',
        tags: ['report writer', 'basics', 'tutorial'],
        estimatedReadTime: '4 min',
        content: `
# Creating Your First Report

## What is the Report Writer?

The Report Writer helps you create structured reports from your conversations and insights. Perfect for:

- Decision documentation
- Learning summaries
- Project retrospectives
- Knowledge transfer

## Starting a New Report

1. Click **Report Writer** in the navigation
2. Click **New Report** or **Start Report**
3. Choose a template (if available) or start blank

## Report Structure

A typical RLC report includes:

### 1. Context
- What situation or decision is this about?
- Why does it matter?
- Who's involved?

### 2. Key Questions
- What questions did you explore?
- What perspectives did you consider?

### 3. Insights & Learning
- What did you learn?
- What patterns emerged?
- What assumptions were challenged?

### 4. Actions & Next Steps
- What will you do differently?
- What specific actions will you take?
- How will you measure success?

## Formatting Your Report

Use the formatting toolbar to:
- Add headings and structure
- Create bullet lists
- Highlight key points
- Insert quotes from conversations

## Saving Reports

- **Auto-save:** Reports save automatically as you type
- **Manual save:** Click the save icon anytime
- **Drafts:** Unfinished reports are saved as drafts

---

**Next:** Learn about [report templates](#report-templates)
        `
      },
    ]
  },

  'archive': {
    title: 'Archive',
    description: 'Manage and search your documents',
    articles: [
      {
        id: 'archive-basics',
        title: 'Using the Archive',
        description: 'How to store, organize, and find your documents',
        tags: ['archive', 'search', 'documents'],
        estimatedReadTime: '4 min',
        content: `
# Using the Archive

## What is the Archive?

The Archive is your personal knowledge base where you can:
- Store documents and files
- Search using natural language (semantic search)
- Organize conversations and reports
- Find information quickly

## Uploading Documents

1. Go to **Archive**
2. Click **Upload Document**
3. Select file(s) from your computer
4. Add optional tags and descriptions
5. Click **Upload**

**Supported formats:**
- PDF documents
- Word documents (.docx)
- Text files (.txt, .md)
- Presentations (.pptx)

## Searching Your Archive

### Keyword Search
Type keywords to find exact matches:
- "project plan 2024"
- "budget analysis"

### Semantic Search
Ask questions in natural language:
- "What did I learn about leadership last month?"
- "Show me documents about team collaboration"
- "Find my notes on the marketing strategy"

The AI understands meaning, not just keywords!

## Organizing Documents

**Tags:**
- Add tags when uploading
- Filter by tags later
- Use consistent tag names

**Projects:**
- Associate documents with projects
- View all project-related materials together
- Track project evolution over time

## Document Actions

For each document you can:
- **View** - Read the full content
- **Download** - Save a local copy
- **Share** - Generate a share link
- **Delete** - Remove from archive

## Best Practices

**Tagging:**
- Use consistent naming (e.g., "Q1-2025" not "Q1 2025")
- Add 3-5 relevant tags per document
- Include project names, topics, and dates

**Organization:**
- Upload regularly, don't let documents pile up
- Add descriptions when uploading
- Review and clean up quarterly

**Search:**
- Start broad, then refine
- Use semantic search for conceptual queries
- Use keyword search for specific terms

---

**Next:** Learn about [advanced archive features](#archive-advanced)
        `
      },
    ]
  },

  'keyboard-shortcuts': {
    title: 'Keyboard Shortcuts',
    description: 'Keyboard shortcuts for faster navigation',
    articles: [
      {
        id: 'shortcuts-reference',
        title: 'Keyboard Shortcuts Reference',
        description: 'Complete list of keyboard shortcuts',
        tags: ['keyboard', 'shortcuts', 'productivity'],
        estimatedReadTime: '2 min',
        content: `
# Keyboard Shortcuts

## Global Shortcuts

| Shortcut | Action |
|----------|--------|
| **?** or **Cmd+Shift+H** | Open Help Center |
| **Cmd+K** (Mac) / **Ctrl+K** (Win) | Quick search |
| **Esc** | Close modals/dialogs |

## Navigation

| Shortcut | Action |
|----------|--------|
| **Cmd+1** | Go to AI Coach |
| **Cmd+2** | Go to Report Writer |
| **Cmd+3** | Go to Archive |

## AI Coach

| Shortcut | Action |
|----------|--------|
| **Cmd+Enter** | Send message |
| **Cmd+↑** | Edit last message |
| **Cmd+/** | Toggle voice input |

## Report Writer

| Shortcut | Action |
|----------|--------|
| **Cmd+S** | Save report |
| **Cmd+B** | Bold text |
| **Cmd+I** | Italic text |
| **Cmd+E** | Export report |

## Archive

| Shortcut | Action |
|----------|--------|
| **Cmd+F** | Focus search box |
| **Cmd+U** | Upload document |
| **↑/↓** | Navigate results |
| **Enter** | Open selected document |

## Tips

- Mac users: **Cmd** = Command key
- Windows/Linux users: **Ctrl** = Control key
- Shortcuts work when focus is on the page (not in input fields, unless specified)

---

**Customize Shortcuts** (Coming soon): Visit Settings to customize your keyboard shortcuts.
        `
      },
    ]
  },

  'troubleshooting': {
    title: 'Troubleshooting',
    description: 'Solutions to common problems',
    articles: [
      {
        id: 'login-issues',
        title: 'Login Problems',
        description: 'Troubleshooting login and authentication issues',
        tags: ['login', 'troubleshooting', 'authentication', 'errors'],
        estimatedReadTime: '3 min',
        content: `
# Troubleshooting Login Problems

## Common Login Issues

### Issue: "Invalid Credentials"

**Symptoms:**
- Error message after entering email/password
- Can't access your account

**Solutions:**
1. **Verify email address** - Check for typos, ensure it's the exact email used during registration
2. **Check password** - Passwords are case-sensitive, ensure Caps Lock is off
3. **Reset password** - Click "Forgot Password?" to receive reset instructions

### Issue: "Account Locked"

**Symptoms:**
- "Account has been locked" message
- Can't log in even with correct credentials

**Cause:** Too many failed login attempts (security feature)

**Solutions:**
1. **Wait 15 minutes** - Account automatically unlocks
2. **Contact administrator** - They can unlock your account immediately
3. **Reset password** - Use "Forgot Password?" to regain access

### Issue: Password Reset Email Not Received

**Solutions:**
1. **Check spam folder** - Email might be filtered
2. **Wait 5 minutes** - Email delivery can be delayed
3. **Verify email address** - Ensure you entered the correct email
4. **Contact support** - If email still doesn't arrive after 10 minutes

### Issue: Login Page Won't Load

**Solutions:**
1. **Check internet connection** - Ensure you're online
2. **Try different browser** - Chrome, Firefox, Safari, Edge all supported
3. **Clear browser cache** - Ctrl+Shift+Delete (or Cmd+Shift+Delete on Mac)
4. **Check URL** - Ensure you're using the correct RLC Spark URL

### Issue: "Session Expired" After Login

**Cause:** Security token expiration

**Solutions:**
1. **Refresh page** - Press F5 or click refresh
2. **Log in again** - This will create a new session
3. **Check system time** - Ensure your computer's date/time is correct

## Still Having Issues?

If none of these solutions work:
1. Note the exact error message
2. Try a different browser
3. Contact your system administrator
4. Include screenshots if possible

---

**Related:** [Account Settings](#account-settings) | [Password Security](#password-security)
        `
      },
      {
        id: 'ai-coach-not-responding',
        title: 'AI Coach Not Responding',
        description: 'What to do when the AI Coach stops responding',
        tags: ['ai coach', 'troubleshooting', 'errors'],
        estimatedReadTime: '3 min',
        content: `
# AI Coach Not Responding

## Symptoms

- Message sent but no response appears
- "Loading..." indicator stays visible
- Error message appears

## Common Causes & Solutions

### 1. Network Connection Issues

**Check:**
- Is your internet connection stable?
- Can you access other websites?

**Solution:**
- Refresh the page (F5)
- Check your network connection
- Try again in a few moments

### 2. Server Timeout

**Cause:** Large or complex queries may take time

**Solution:**
- Wait 30-60 seconds for complex questions
- Break very long messages into smaller parts
- Simplify your question if possible

### 3. Session Expired

**Symptoms:**
- "Unauthorized" or "Session expired" error
- Redirected to login page

**Solution:**
- Log in again
- Your conversation will be saved
- Continue where you left off

### 4. Rate Limiting

**Cause:** Too many messages sent rapidly

**Solution:**
- Wait 1-2 minutes
- Continue conversation at a natural pace
- This prevents system overload

## Best Practices to Avoid Issues

**Pace Your Conversation:**
- Allow time for thoughtful responses
- The coach is designed for reflection, not rapid-fire Q&A

**Keep Messages Reasonable:**
- Aim for 1-3 paragraphs per message
- Very long messages may timeout

**Save Regularly:**
- Conversations auto-save, but you can also manually save
- Important insights should be captured in reports

## When to Contact Support

Contact your administrator if:
- Issue persists after 15 minutes
- You see repeated error messages
- Multiple features aren't working

Include:
- Screenshot of the error
- What you were doing when it occurred
- Your browser and operating system

---

**Related:** [AI Coach Basics](#ai-coach-basics) | [Technical Issues](#technical-issues)
        `
      },
      {
        id: 'performance-issues',
        title: 'Performance & Loading Issues',
        description: 'Troubleshooting slow loading and performance problems',
        tags: ['performance', 'slow', 'troubleshooting'],
        estimatedReadTime: '3 min',
        content: `
# Performance & Loading Issues

## Slow Page Loading

### Check Your Connection

**Test your internet speed:**
- Visit speedtest.net
- Minimum recommended: 5 Mbps download

**Solutions:**
- Close unnecessary browser tabs
- Disconnect from VPN (if not required)
- Move closer to WiFi router
- Use wired connection if available

### Browser Issues

**Try these steps:**

1. **Clear browser cache:**
   - Chrome: Ctrl+Shift+Delete
   - Select "Cached images and files"
   - Clear last 24 hours

2. **Disable browser extensions:**
   - Some extensions interfere with web apps
   - Try incognito/private mode
   - Disable ad blockers temporarily

3. **Update your browser:**
   - Use the latest version
   - Recommended: Chrome 90+, Firefox 88+, Safari 14+

### Hardware Limitations

**If you're on an older computer:**
- Close other applications
- Use browser with fewer tabs
- Consider using Safari (more efficient on Mac)

## Slow AI Responses

### Normal vs. Slow

**Normal response times:**
- Simple questions: 3-10 seconds
- Complex reflections: 15-30 seconds
- Very detailed analysis: 30-60 seconds

**Too slow (troubleshoot):**
- Over 2 minutes with no response
- "Loading..." indicator frozen

**Solutions:**
- Refresh the page
- Break question into smaller parts
- Try again in a few minutes

## Application Freezing

**If the interface becomes unresponsive:**

1. **Wait 30 seconds** - May be processing
2. **Refresh page** - Press F5
3. **Clear cache** - See above
4. **Try different browser**
5. **Restart browser** - Close completely and reopen

## Mobile Performance

**On mobile devices:**
- Loading may take longer on cellular data
- Use WiFi when possible
- Close background apps
- Ensure 20%+ battery (low power mode can slow down)

## System Status

Check if there are known issues:
- Contact your administrator
- Check for maintenance notifications
- Try again in 15 minutes

---

**Related:** [Browser Compatibility](#browser-compatibility) | [System Requirements](#system-requirements)
        `
      },
    ]
  }
};

// ============================================
// SEARCH FUNCTIONALITY
// ============================================

/**
 * Search help articles by keyword or phrase
 *
 * @param {string} query - Search query
 * @returns {Array} - Array of matching articles with category info
 */
export function searchHelpArticles(query) {
  if (!query || query.trim().length < 2) {
    return [];
  }

  const results = [];
  const lowerQuery = query.toLowerCase().trim();

  Object.entries(HELP_CONTENT).forEach(([categoryKey, category]) => {
    category.articles.forEach(article => {
      let score = 0;

      // Title match (highest weight)
      if (article.title.toLowerCase().includes(lowerQuery)) {
        score += 100;
      }

      // Tag match
      if (article.tags.some(tag => tag.toLowerCase().includes(lowerQuery))) {
        score += 50;
      }

      // Description match
      if (article.description.toLowerCase().includes(lowerQuery)) {
        score += 30;
      }

      // Content match
      if (article.content.toLowerCase().includes(lowerQuery)) {
        score += 10;
      }

      if (score > 0) {
        results.push({
          ...article,
          categoryKey,
          categoryTitle: category.title,
          score
        });
      }
    });
  });

  // Sort by relevance score
  return results.sort((a, b) => b.score - a.score);
}

/**
 * Get article by ID across all categories
 *
 * @param {string} articleId - Article ID to find
 * @returns {Object|null} - Article with category info or null
 */
export function getArticleById(articleId) {
  for (const [categoryKey, category] of Object.entries(HELP_CONTENT)) {
    const article = category.articles.find(a => a.id === articleId);
    if (article) {
      return {
        ...article,
        categoryKey,
        categoryTitle: category.title
      };
    }
  }
  return null;
}

/**
 * Get all articles in a category
 *
 * @param {string} categoryKey - Category key
 * @returns {Array} - Array of articles
 */
export function getArticlesByCategory(categoryKey) {
  const category = HELP_CONTENT[categoryKey];
  return category ? category.articles : [];
}

/**
 * Get article count by category
 *
 * @returns {Object} - Category keys with article counts
 */
export function getArticleCounts() {
  const counts = {};
  Object.entries(HELP_CONTENT).forEach(([key, category]) => {
    counts[key] = category.articles.length;
  });
  return counts;
}

export default HELP_CONTENT;
